import { useEffect } from 'react';
import { Modal } from '@shared/components/modal/Modal';
import styles from './ReservationTimelineBookModal.scss';
import { useAvailabilityDrawerContext } from '../reservations/concierge/state/AvailabilityDrawerContext';
import {
  MultiTableBookingForm,
  type MultiTableReservationFormData,
} from '../reservations/concierge/MultiTableBookingForm';
import {
  checkMultiTableReservationForOverlaps,
  type OverlappingReservation,
} from '../reservations/concierge/apiHelpers';
import { useRestaurant } from '../context/useRestaurant';
import { useConciergeAvailabilityContext } from '../reservations/concierge/ConciergeAvailabilityContext';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { ConciergeAvailabilityContextProvider } from '../reservations/concierge/ConciergeAvailabilityContext';
import { AvailabilityDrawerContextProvider } from '../reservations/concierge/state/AvailabilityDrawerContext';
import { ConciergeDrawer } from '../reservations/concierge/ConciergeDrawer';
import { StripeElements } from '@components/stripe/StripeElements';
import { GodModeOverbookingWarningModal } from '../reservations/shared/GodModeOverbookingWarningModal';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { ISOTimeFloorToQuarterHour } from '@shared/utils/time';

type ReservationTimelineBookModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  selectedDate?: Date;
  selectedTime?: string;
  selectedDuration?: number;
  selectedTableId?: string;
  selectedFloorPlanId?: string;
};

export const ReservationTimelineBookModal = ({
  isOpen,
  closeModal,
  selectedDate,
  selectedTime,
  selectedDuration,
  selectedTableId,
  selectedFloorPlanId,
}: ReservationTimelineBookModalProps) => {
  const { id: restaurantId } = useRestaurant();

  return (
    <ConciergeAvailabilityContextProvider>
      <AvailabilityDrawerContextProvider>
        <StripeElements>
          <ReservationTimelineBookModalContent
            isOpen={isOpen}
            closeModal={closeModal}
            restaurantId={restaurantId}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedDuration={selectedDuration}
            selectedTableId={selectedTableId}
            selectedFloorPlanId={selectedFloorPlanId}
          />
          <ConciergeDrawer />
        </StripeElements>
      </AvailabilityDrawerContextProvider>
    </ConciergeAvailabilityContextProvider>
  );
};

const ReservationTimelineBookModalContent = ({
  isOpen,
  closeModal,
  restaurantId,
  selectedDate,
  selectedTime,
  selectedDuration,
  selectedTableId,
  selectedFloorPlanId,
}: {
  isOpen: boolean;
  closeModal: () => void;
  restaurantId: string;
  selectedDate?: Date;
  selectedTime?: string;
  selectedDuration?: number;
  selectedTableId?: string;
  selectedFloorPlanId?: string;
}) => {
  const {
    selectedFloorPlanTableIds,
    selectedFloorPlanTables,
    setSelectedFloorPlanTableIds,
    setSelectedFloorPlanId,
    setSelectedDate,
    setSelectedTime,
  } = useConciergeAvailabilityContext();

  const { openDrawer } = useAvailabilityDrawerContext();

  // Create the callback function inside the context provider
  const openMultiTableBookReservationDrawer = ({
    date,
    time,
    guestCount,
    duration,
  }: MultiTableReservationFormData) => {
    closeModal();
    openDrawer({
      date,
      time,
      guestCount,
      duration,
      tableIds: selectedFloorPlanTableIds,
      listingPrice: 0, // Reservation price doesn't come from the listing, admins must set a custom price.
      multiTable: true,
    });
  };

  useEffect(() => {
    if (isOpen) {
      if (selectedDate) setSelectedDate(selectedDate);
      if (selectedTime) setSelectedTime(selectedTime);
      if (selectedFloorPlanId) setSelectedFloorPlanId(selectedFloorPlanId);
      if (selectedTableId) setSelectedFloorPlanTableIds([selectedTableId]);
    }
  }, [
    isOpen,
    selectedDate,
    selectedTime,
    selectedFloorPlanId,
    selectedTableId,
  ]);

  // Format the date and time for the form
  const formattedDate = selectedDate
    ? toISODateFormat(selectedDate)
    : undefined;
  const formattedTime = selectedTime
    ? ISOTimeFloorToQuarterHour(selectedTime)
    : undefined;

  const {
    isOpen: isUnassignmentWarningModalOpen,
    open: openUnassignmentWarningModal,
    close: closeUnassignmentWarningModal,
    data: modalData,
  } = useIsOpenWithData<{
    formData: MultiTableReservationFormData;
    overlappingReservations: OverlappingReservation[];
  }>();

  const handleReserveClickMultiTableWithSelectedTables = async (
    formData: MultiTableReservationFormData,
  ) => {
    const durationInMinutes =
      selectedDuration !== undefined
        ? selectedDuration
        : Number(formData.duration);
    const { wouldOverlap, overlappingReservations } =
      await checkMultiTableReservationForOverlaps(restaurantId, {
        date: formData.date,
        duration: durationInMinutes,
        time: formData.time,
        floorPlanTableIds: selectedFloorPlanTableIds,
      });
    if (wouldOverlap) {
      openUnassignmentWarningModal({ formData, overlappingReservations });
    } else {
      openMultiTableBookReservationDrawer(formData);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={closeModal} title="" subtitle="">
      <div className={styles.modalContent}>
        <div className={styles.multiTableFormContainer}>
          <MultiTableBookingForm
            tableLabelPosition="bottom"
            submitButtonText="Book Tables"
            handleSubmitClick={handleReserveClickMultiTableWithSelectedTables}
            selectedFloorPlanTables={selectedFloorPlanTables}
            defaultDate={formattedDate}
            defaultTime={formattedTime}
            defaultDuration={selectedDuration}
          />
        </div>
      </div>
      {modalData && (
        <GodModeOverbookingWarningModal
          isOpen={isUnassignmentWarningModalOpen}
          onClose={closeUnassignmentWarningModal}
          onConfirm={openMultiTableBookReservationDrawer}
          formData={modalData.formData}
          overlappingReservations={modalData.overlappingReservations}
        />
      )}
    </Modal>
  );
};
